import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import "../../../i18n";
import { Notice, StatusPill } from "../../../design/components";
import { ApiError } from "../../../api/client";
import { dateTime, relative } from "../../../lib/format";
import type { AgentSummary, IssuedAgent } from "../../../api/types";
import { CopyButton } from "../CopyButton";
import { agentsKey, issueAgentToken, revokeAgent, useAgents } from "./agentsApi";
import styles from "./connection.module.css";

export const TOKEN_ONCE = "This token is shown once. It is stored only as a hash.";

export type AgentPill = { status: string; label: string };

/** Silence is the company's own threshold, the same one the entity's health uses. */
export function agentPill(
  agent: AgentSummary,
  silenceThresholdMinutes: number,
  now: Date = new Date(),
): AgentPill {
  if (agent.revokedAt) return { status: "failed", label: "Revoked" };
  if (!agent.lastSeenAt) return { status: "queued", label: "Never checked in" };
  const age = now.getTime() - new Date(agent.lastSeenAt).getTime();
  return age <= silenceThresholdMinutes * 60_000
    ? { status: "ok", label: "Healthy" }
    : { status: "silent", label: "Not checking in" };
}

const COPY = {
  agent: {
    title: "On-site agents",
    none: "No agent has been given a token for this company yet.",
    name: "Agent name",
    column: "Agent",
    tokenLabel: "New agent token",
    copy: "Copy the agent token",
    dialog: "Issue an agent token",
    revokeDialog: "Revoke an agent",
    revokeNote: "will stop being able to reach the hub. Its token cannot be restored; issue a new one to reconnect that machine.",
    failed: "The agent could not be revoked.",
  },
  push: {
    title: "Push tokens",
    none: "No push token has been issued for this company yet.",
    name: "Token name",
    column: "Token",
    tokenLabel: "New push token",
    copy: "Copy the push token",
    dialog: "Issue a push token",
    revokeDialog: "Revoke a push token",
    revokeNote: "will be refused by the hub at once. Its token cannot be restored; issue a new one for the ERP.",
    failed: "The token could not be revoked.",
  },
} as const;

/** Both kinds share this panel; each field resolves `companies.agentTokens.<kind>.<field>`. */
function copyFor(kind: "agent" | "push", t: (key: string, fallback: string) => string) {
  const base = COPY[kind];
  const out = {} as { -readonly [K in keyof typeof base]: string };
  for (const field of Object.keys(base) as (keyof typeof base)[]) {
    out[field] = t(`companies.agentTokens.${kind}.${field}`, base[field]);
  }
  return out;
}

export function AgentTokensPanel({
  entityId,
  silenceThresholdMinutes,
  canManage,
  kind = "agent",
}: {
  entityId: string;
  silenceThresholdMinutes: number;
  canManage: boolean;
  kind?: "agent" | "push";
}) {
  const { t } = useTranslation();
  const copy = copyFor(kind, (key, fallback) => t(key, fallback));
  const queryClient = useQueryClient();
  const agents = useAgents(entityId);
  const [naming, setNaming] = useState(false);
  const [label, setLabel] = useState("");
  const [issuing, setIssuing] = useState(false);
  const [token, setToken] = useState<IssuedAgent | null>(null);
  const [revoking, setRevoking] = useState<AgentSummary | null>(null);
  const [revokeBusy, setRevokeBusy] = useState(false);
  const [error, setError] = useState("");

  async function refresh(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: agentsKey(entityId) });
  }

  async function issue(): Promise<void> {
    setIssuing(true);
    setError("");
    try {
      setToken(await issueAgentToken(entityId, label.trim(), kind));
      setNaming(false);
      setLabel("");
      await refresh();
    } catch (exc) {
      setError(
        exc instanceof ApiError ? exc.message : t("companies.agentTokens.issueError", "The token could not be issued."),
      );
    } finally {
      setIssuing(false);
    }
  }

  async function revoke(agent: AgentSummary): Promise<void> {
    if (revokeBusy) return;
    setRevokeBusy(true);
    setError("");
    try {
      await revokeAgent(entityId, agent.id);
      setRevoking(null);
      await refresh();
    } catch (exc) {
      setError(
        exc instanceof ApiError
          ? exc.message
          : t("companies.agentTokens.revokeError", "The agent could not be revoked."),
      );
    } finally {
      setRevokeBusy(false);
    }
  }

  // Both kinds share one list; a payload without `kind` is an agent.
  const rows = (agents.data ?? []).filter((agent) => (agent.kind ?? "agent") === kind);

  return (
    <section className="card card-tight">
      <div className="card-head">
        <h2 className="grow">{copy.title}</h2>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          disabled={!canManage}
          onClick={() => {
            setToken(null);
            setNaming(true);
          }}
        >
          {t("companies.agentTokens.issueButton", "Issue a token")}
        </button>
      </div>

      {token ? (
        <div className="card-body">
          <div className={styles.tokenBox} role="group" aria-label={copy.tokenLabel}>
            <label htmlFor="agent-token">{`Token for ${token.label}`}</label>
            <div className={styles.tokenRow}>
              <input id="agent-token" className="input mono" type="text" readOnly value={token.token} />
              <CopyButton value={token.token} label={copy.copy} />
            </div>
            <p className={styles.warn}>{t("companies.agentTokens.tokenOnce", TOKEN_ONCE)}</p>
            <button type="button" className="btn btn-sm" onClick={() => setToken(null)}>
              {t("companies.agentTokens.doneButton", "Done")}
            </button>
          </div>
        </div>
      ) : null}

      {error ? (
        <div className="card-body">
          <Notice kind="fail">{error}</Notice>
        </div>
      ) : null}

      {rows.length === 0 ? (
        <div className="card-body">
          <Notice kind="info">{copy.none}</Notice>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>{copy.column}</th>
                <th>Status</th>
                <th>Last seen</th>
                <th>Version</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((agent) => {
                const pill = agentPill(agent, silenceThresholdMinutes);
                return (
                  <tr key={agent.id}>
                    <td>
                      <span className={styles.agentName}>{agent.label}</span>
                      <span className="mono">{agent.tokenPrefix}</span>
                    </td>
                    <td>
                      <StatusPill {...pill} />
                    </td>
                    <td className="nowrap">
                      {agent.lastSeenAt ? (
                        <span title={dateTime(agent.lastSeenAt)}>{relative(agent.lastSeenAt)}</span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="nowrap">{agent.version ?? "—"}</td>
                    <td>
                      {agent.revokedAt ? null : (
                        <button
                          type="button"
                          className="btn btn-sm"
                          disabled={!canManage}
                          onClick={() => setRevoking(agent)}
                        >
                          {t("companies.agentTokens.revokeButton", "Revoke {{name}}", { name: agent.label })}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {naming ? (
        <div className={styles.dialog} role="dialog" aria-label={copy.dialog}>
          <div className={styles.field}>
            <label htmlFor="agent-label">{copy.name}</label>
            <input
              id="agent-label"
              className="input"
              type="text"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
            />
          </div>
          <div className={styles.dialogFoot}>
            <button type="button" className="btn" onClick={() => setNaming(false)}>
              {t("companies.agentTokens.cancelButton", "Cancel")}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={issuing || label.trim() === ""}
              onClick={() => void issue()}
            >
              {t("companies.agentTokens.issueTokenButton", "Issue token")}
            </button>
          </div>
        </div>
      ) : null}

      {revoking ? (
        <div className={styles.dialog} role="dialog" aria-label={copy.revokeDialog}>
          <p>
            {revoking.label} {copy.revokeNote}
          </p>
          <div className={styles.dialogFoot}>
            <button type="button" className="btn" disabled={revokeBusy} onClick={() => setRevoking(null)}>
              {t("companies.agentTokens.cancelButton", "Cancel")}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={revokeBusy}
              onClick={() => void revoke(revoking)}
            >
              {t("companies.agentTokens.revokeConfirmButton", "Revoke")}
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
